import { ReactNode } from "react";
import { Col, Skeleton, Icons, Icon, Flex, Button, Popover, Division, Markdown, Tooltip, Avatar, Span } from "@ir/ui";
import { DeductionInformationLabels } from "./utils/deductionInformationLabel.enum";
import { PriorityChip, ProgressChip, ValidityChip } from "@/presentation/deduction/components";
import { Deduction } from "@/core/entities/deduction";
import { DeductionUserDisplay } from "../../DeductionUserDisplay";

interface DrawerInfoColProps {
  label: string;
  value?: string | number | ReactNode;
  name?: string | ReactNode;
  fontBold?: boolean;
  children?: ReactNode;
  span?: number;
  md?: number;
  isLoading?: boolean;
  deductionSubTypeHelpText?: string | null;
  deduction: Deduction;
}

const InfoPopoverContent = ({ value, isMarkdown = false }: { value: string; isMarkdown?: boolean }) => {
  return <Division className="deduction-info-popover-content">{isMarkdown ? <Markdown content={value} /> : value}</Division>;
};

const InfoPopover = ({ value, isMarkdown = false, className }: { value: ReactNode; isMarkdown?: boolean; className?: string }) => {
  return (
    <Popover
      content={<InfoPopoverContent value={value as string} isMarkdown={isMarkdown} />}
      placement="bottom"
      className="deduction-info-popover"
      isNonModal={true}
    >
      <Button noStyle className={className}>
        <Icon name={Icons.HelpCircle} size="small" color="grey" />
      </Button>
    </Popover>
  );
};

const DrawerInfoCol = ({
  label,
  value,
  children,
  fontBold = true,
  span = 6,
  md = 3,
  isLoading = false,
  deductionSubTypeHelpText,
  name,
  deduction,
}: DrawerInfoColProps) => {
  const currentProgress = deduction?.stages.find(stage => stage.isCurrent);
  return (
    <Col
      span={span}
      md={md}
      style={{
        marginBottom:
          label === DeductionInformationLabels.Priority || label === DeductionInformationLabels.Validity || label === DeductionInformationLabels.Status
            ? "0"
            : "27px",
      }}
    >
      <Flex className="text-grey-600 text-xs" align="center" gap={0.25}>
        <Span style={{ marginBottom: "2px" }} className="font-medium">
          {label}
        </Span>
        {!isLoading && label === DeductionInformationLabels.TotalAmount && (
          <Tooltip content={"As per Document"} placement="top">
            <Button noStyle style={{ cursor: "default" }}>
              <Icon name={Icons.HelpCircle} size="small" color="grey" style={{ width: "14px", height: "14px" }} />
            </Button>
          </Tooltip>
        )}
        {!isLoading && label === DeductionInformationLabels.DeductedAmount && (
          <Tooltip content={"Total of Charge Breakdown"} placement="top">
            <Button noStyle style={{ cursor: "default" }}>
              <Icon name={Icons.HelpCircle} size="small" color="grey" style={{ width: "14px", height: "14px" }} />
            </Button>
          </Tooltip>
        )}
      </Flex>
      <Flex
        className={`text-primary-900 ${fontBold ? "font-medium" : "font-normal"} text-sm`}
        align="center"
        gap={label === DeductionInformationLabels.Priority ? 0.3 : 0.5}
      >
        {!isLoading && label === DeductionInformationLabels.Priority && value && (
          <PriorityChip
            priorityKey={value as string}
            priorityName={name as string}
            withBorder={true}
            withIcon={true}
            withBackground={true}
            size="small"
            shape="circle"
          />
        )}
        {!isLoading && label === DeductionInformationLabels.Validity && value && (
          <Flex align="center" justifyContent="center" gap={0.5}>
            <ValidityChip
              validityKey={value as string}
              validityName={name as string}
              withBorder={true}
              withIcon={false}
              withBackground={true}
              size="small"
              shape="circle"
            />
          </Flex>
        )}
        {!isLoading && label === DeductionInformationLabels.Status && value && (
          <ProgressChip
            stageKey={currentProgress?.stageKey ?? ""}
            stageName={currentProgress?.stageName ?? ""}
            statusKey={deduction?.status?.statusKey ?? ""}
            statusName={deduction?.status?.statusName ?? ""}
            withBorder={true}
            withBackground={true}
            withIcon={false}
            size="small"
            shape="circle"
          />
        )}
        {!isLoading && label === DeductionInformationLabels.Analyst && deduction?.id != null && (
          <Flex align="center" justifyContent="start" gap={0.5} wrap="no-wrap" style={{ maxWidth: "100%" }}>
            <Avatar
              src={deduction?.analystUserAvatar}
              name={deduction?.analystUserFullName ?? ""}
              size="extra-small"
              shape="circle"
              isDeleteUser={deduction?.isAnalystDeleted}
              title={deduction?.isAnalystDeleted ? "Removed User" : (deduction?.analystUserFullName ?? "Unassigned")}
            />

            <Division className="name-display-container">
              <DeductionUserDisplay name={deduction?.analystUserFullName} />
            </Division>
          </Flex>
        )}

        {!isLoading && label === DeductionInformationLabels.Approver && deduction?.id != null && (
          <Flex align="center" justifyContent="start" gap={0.5} wrap="no-wrap" style={{ maxWidth: "100%" }}>
            <Avatar
              src={deduction?.approverUserAvatar}
              name={deduction?.approverUserFullName ?? ""}
              size="extra-small"
              shape="circle"
              isDeleteUser={deduction?.isApproverDeleted}
              title={deduction?.isApproverDeleted ? "Removed User" : (deduction?.approverUserFullName ?? "Unassigned")}
            />

            <Division className="name-display-container">
              <DeductionUserDisplay name={deduction?.approverUserFullName} />
            </Division>
          </Flex>
        )}

        {isLoading ? (
          <Skeleton
            height={9}
            width={label === DeductionInformationLabels.Description ? 360 : 120}
            animation="wave"
            variant="rounded"
            className={`mb-2 ${
              DeductionInformationLabels.Validity === label ||
              DeductionInformationLabels.Priority === label ||
              DeductionInformationLabels.Status === label ||
              DeductionInformationLabels.Approver === label ||
              DeductionInformationLabels.Analyst === label
                ? "mt-2"
                : "mt-1"
            }`}
          />
        ) : (
          <>
            {((DeductionInformationLabels.Validity !== label &&
              DeductionInformationLabels.Priority !== label &&
              DeductionInformationLabels.Status !== label &&
              DeductionInformationLabels.Approver !== label &&
              DeductionInformationLabels.Analyst !== label) ||
              deduction?.id == null) &&
              (value ?? children ?? "-")}
          </>
        )}
        {!isLoading && label === DeductionInformationLabels.DeductionSubType && value && deductionSubTypeHelpText && (
          <InfoPopover value={deductionSubTypeHelpText} isMarkdown={true} className="deduction-info-popover-button" />
        )}
      </Flex>
    </Col>
  );
};

export default DrawerInfoCol;
